import React, { useMemo, useState } from 'react';
import { NavActions } from '../components/NavBar/NavActions';
import { MobileUnsupported } from '../components/MobileUnsupported';
import { Button } from '../components/SubscriptionAppButton';
import { Input } from '../components/SubscriptionAppInput';
import { Card } from '../components/SubscriptionAppCard';
import { Divider } from '../components/SubscriptionAppDivider';
import { IconButton } from '../components/SubscriptionAppIconButton';
import { Zap, Filter, ChevronRight, Trash2, Info, Plus, Minus } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTags } from '../contexts/TagsContext';
// --- Types ---
type TriggerType = 'Event-based' | 'Scheduled (Daily)';
type ConditionLogic = 'AND' | 'OR';
interface Condition {
  id: string;
  field: string;
  operator: string;
  value: string;
  unit?: string; // For date-relative fields
}
interface ConditionGroup {
  id: string;
  logic: ConditionLogic;
  conditions: Condition[];
}
interface Action {
  id: string;
  type: string;
  value: string; // Primary value (e.g. tag name, event name)
  secondaryValue?: string; // For things like assignee
}
// --- Constants & Options ---
const TRIGGER_EVENTS = ['Order Completed', 'Subscription Created', 'Subscription Cancelled', 'Subscription Paused', 'Subscription Reactivated', 'Box Edited', 'Payment Failed', 'Payment Recovered', 'Portal Login', 'Email Opened (Klaviyo)', 'Tag Added', 'Tag Removed'];
const CONDITION_FIELDS = {
  Lifecycle: ['Order Count', 'Subscription Status', 'Member Tenure', 'Plan Name', 'Signup Date', 'Subscription Anniversary'],
  Behaviour: ['Portal Logins Last 30d', 'Box Edits Last 90d', 'Skips Last 90d', 'Last Portal Visit', 'Email Opens Last 30d', 'Days Since Last Login'],
  Value: ['Lifetime Spend', 'Average Order Value', 'Spend Trend', 'Current Plan Price'],
  Product: ['Top Varietal % (Red)', 'Top Varietal % (White)', 'Varietals Tried Count', 'Last Product Swapped', 'Most Ordered Product'],
  Time: ['Days Since Last Order', 'Days Until Next Billing', 'Month of Year', 'Day of Week']
};
const OPERATORS = {
  numeric: ['equals', 'does not equal', 'is greater than', 'is less than', 'is greater than or equal to', 'is less than or equal to', 'is between'],
  text: ['equals', 'does not equal', 'contains', 'does not contain'],
  date: ['is before', 'is after', 'is within last', 'is more than ago']
};
const ACTION_TYPES = ['Add Tag', 'Remove Tag', 'Send Klaviyo Event', 'Create Task', 'Adjust Loyalty Points', 'Add Note to Contact'];
const TAG_OPTIONS = ['Loyal', 'VIP', 'At Risk', 'New Member', 'Churned', 'High Value', 'Low Engagement', 'Wine Enthusiast'];
const ASSIGNEE_OPTIONS = ['Team', 'Sarah', 'Mike', 'Alex'];
// --- Styling Constants ---
const selectClasses = 'w-full h-[40px] bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200 cursor-pointer';
const conditionSelectClasses = 'h-[36px] bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200 cursor-pointer';
const conditionInputClasses = 'h-[36px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200';
const selectPadding: React.CSSProperties = {
  paddingLeft: 12,
  paddingRight: 40
};
// --- Helper Functions ---
const getFieldType = (field: string) => {
  if (['Order Count', 'Member Tenure', 'Portal Logins Last 30d', 'Box Edits Last 90d', 'Skips Last 90d', 'Days Since Last Login', 'Lifetime Spend', 'Average Order Value', 'Current Plan Price', 'Top Varietal % (Red)', 'Top Varietal % (White)', 'Varietals Tried Count', 'Days Since Last Order', 'Days Until Next Billing', 'Month of Year'].includes(field)) return 'numeric';
  if (['Signup Date', 'Subscription Anniversary', 'Last Portal Visit'].includes(field)) return 'date';
  return 'text';
};
const getOperators = (field: string) => {
  const type = getFieldType(field);
  return OPERATORS[type] || OPERATORS.text;
};
const generateId = () => Math.random().toString(36).substr(2, 9);
export function CreateAutomationRulePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    allTags
  } = useTags();
  // Check if we're duplicating a rule (name passed via router state)
  const duplicatedName = (location.state as any)?.duplicatedRuleName || '';
  // --- State ---
  const [ruleName, setRuleName] = useState(duplicatedName);
  const [description, setDescription] = useState('');
  const [triggerType, setTriggerType] = useState<TriggerType>('Event-based');
  const [event, setEvent] = useState('Order Completed');
  const [conditionGroups, setConditionGroups] = useState<ConditionGroup[]>([{
    id: 'group-1',
    logic: 'AND',
    conditions: [{
      id: 'c1',
      field: 'Order Count',
      operator: 'is greater than',
      value: '1'
    }, {
      id: 'c2',
      field: 'Subscription Status',
      operator: 'equals',
      value: 'Active'
    }]
  }, {
    id: 'group-2',
    logic: 'OR',
    conditions: [{
      id: 'c3',
      field: 'Member Tenure',
      operator: 'is greater than',
      value: '12 months'
    }]
  }]);
  const [metActions, setMetActions] = useState<Action[]>([{
    id: 'a1',
    type: 'Add Tag',
    value: 'Loyal'
  }, {
    id: 'a2',
    type: 'Send Klaviyo Event',
    value: 'loyal_member_milestone'
  }]);
  const [exitActions, setExitActions] = useState<Action[]>([{
    id: 'a3',
    type: 'Remove Tag',
    value: 'Loyal'
  }]);
  // --- Handlers ---
  const addConditionGroup = () => {
    setConditionGroups([...conditionGroups, {
      id: generateId(),
      logic: 'AND',
      conditions: [{
        id: generateId(),
        field: 'Order Count',
        operator: 'is greater than',
        value: ''
      }]
    }]);
  };
  const addCondition = (groupId: string) => {
    setConditionGroups(conditionGroups.map(g => {
      if (g.id === groupId) {
        return {
          ...g,
          conditions: [...g.conditions, {
            id: generateId(),
            field: 'Order Count',
            operator: 'is greater than',
            value: ''
          }]
        };
      }
      return g;
    }));
  };
  const updateCondition = (groupId: string, conditionId: string, field: keyof Condition, value: string) => {
    setConditionGroups(conditionGroups.map(g => {
      if (g.id === groupId) {
        return {
          ...g,
          conditions: g.conditions.map(c => {
            if (c.id === conditionId) {
              const newCondition = {
                ...c,
                [field]: value
              };
              // Reset operator/value if field changes
              if (field === 'field') {
                newCondition.operator = getOperators(value)[0];
                newCondition.value = '';
                newCondition.unit = 'days';
              }
              return newCondition;
            }
            return c;
          })
        };
      }
      return g;
    }));
  };
  const deleteCondition = (groupId: string, conditionId: string) => {
    // Prevent deleting the last condition on the page
    const totalConditions = conditionGroups.reduce((acc, g) => acc + g.conditions.length, 0);
    if (totalConditions <= 1) return;
    setConditionGroups(conditionGroups.map(g => {
      if (g.id === groupId) {
        const newConditions = g.conditions.filter(c => c.id !== conditionId);
        // If group becomes empty, mark for deletion (by returning null here and filtering later)
        if (newConditions.length === 0) return null;
        return {
          ...g,
          conditions: newConditions
        };
      }
      return g;
    }).filter(Boolean) as ConditionGroup[]);
  };
  const toggleGroupLogic = (groupId: string) => {
    setConditionGroups(conditionGroups.map(g => g.id === groupId ? {
      ...g,
      logic: g.logic === 'AND' ? 'OR' : 'AND'
    } : g));
  };
  const addAction = (isMet: boolean) => {
    const newAction = {
      id: generateId(),
      type: 'Add Tag',
      value: ''
    };
    if (isMet) {
      setMetActions([...metActions, newAction]);
    } else {
      setExitActions([...exitActions, newAction]);
    }
  };
  const updateAction = (isMet: boolean, id: string, field: keyof Action, value: string) => {
    const updater = (actions: Action[]) => actions.map(a => a.id === id ? {
      ...a,
      [field]: value
    } : a);
    if (isMet) setMetActions(updater(metActions));else setExitActions(updater(exitActions));
  };
  const deleteAction = (isMet: boolean, id: string) => {
    if (isMet && metActions.length <= 1) return; // Prevent deleting last met action
    if (isMet) setMetActions(metActions.filter(a => a.id !== id));else setExitActions(exitActions.filter(a => a.id !== id));
  };
  // --- Render Helpers ---
  const renderConditionValueInput = (condition: Condition, groupId: string) => {
    const {
      field,
      operator,
      value,
      unit
    } = condition;
    const fieldType = getFieldType(field);
    if (field === 'Subscription Status') {
      return <select className={`w-32 ${conditionSelectClasses}`} style={selectPadding} value={value} onChange={e => updateCondition(groupId, condition.id, 'value', e.target.value)} data-id="element-6095">
          <option value="" data-id="element-6096">Select...</option>
          <option value="Active" data-id="element-6097">Active</option>
          <option value="Paused" data-id="element-6098">Paused</option>
          <option value="Cancelled" data-id="element-6099">Cancelled</option>
          <option value="Expired" data-id="element-6100">Expired</option>
        </select>;
    }
    if (field === 'Spend Trend') {
      return <select className={`w-32 ${conditionSelectClasses}`} style={selectPadding} value={value} onChange={e => updateCondition(groupId, condition.id, 'value', e.target.value)} data-id="element-6101">
          <option value="" data-id="element-6102">Select...</option>
          <option value="Up" data-id="element-6103">Up</option>
          <option value="Down" data-id="element-6104">Down</option>
          <option value="Stable" data-id="element-6105">Stable</option>
        </select>;
    }
    if (operator === 'is within last' || operator === 'is more than ago') {
      return <div className="flex gap-2" data-id="element-6106">
          <input type="number" className={`w-20 ${conditionInputClasses}`} value={value} onChange={e => updateCondition(groupId, condition.id, 'value', e.target.value)} data-id="element-6107" />
          <select className={`w-24 ${conditionSelectClasses}`} style={selectPadding} value={unit || 'days'} onChange={e => updateCondition(groupId, condition.id, 'unit', e.target.value)} data-id="element-6108">
            <option value="days" data-id="element-6109">Days</option>
            <option value="weeks" data-id="element-6110">Weeks</option>
            <option value="months" data-id="element-6111">Months</option>
          </select>
        </div>;
    }
    return <input type={fieldType === 'numeric' ? 'number' : 'text'} className={`w-32 ${conditionInputClasses}`} value={value} onChange={e => updateCondition(groupId, condition.id, 'value', e.target.value)} data-id="element-6112" />;
  };
  const renderActionInput = (action: Action, isMet: boolean) => {
    const {
      type,
      value,
      secondaryValue
    } = action;
    if (type === 'Add Tag' || type === 'Remove Tag') {
      return <select className={`w-48 ${conditionSelectClasses}`} style={selectPadding} value={value} onChange={e => updateAction(isMet, action.id, 'value', e.target.value)} data-id="element-6113">
          <option value="" data-id="element-6114">Select Tag...</option>
          {allTags.map(t => <option key={t.id} value={t.label} data-id="element-6115">
              {t.label}
            </option>)}
        </select>;
    }
    if (type === 'Send Klaviyo Event') {
      return <input type="text" placeholder="Event Name" className={`flex-1 ${conditionInputClasses}`} value={value} onChange={e => updateAction(isMet, action.id, 'value', e.target.value)} data-id="element-6116" />;
    }
    if (type === 'Create Task') {
      return <div className="flex gap-2 flex-1" data-id="element-6117">
          <input type="text" placeholder="Task Title" className={`flex-1 ${conditionInputClasses}`} value={value} onChange={e => updateAction(isMet, action.id, 'value', e.target.value)} data-id="element-6118" />
          <select className={`w-32 ${conditionSelectClasses}`} style={selectPadding} value={secondaryValue || ''} onChange={e => updateAction(isMet, action.id, 'secondaryValue', e.target.value)} data-id="element-6119">
            <option value="" data-id="element-6120">Assignee...</option>
            {ASSIGNEE_OPTIONS.map(a => <option key={a} value={a} data-id="element-6121">
                {a}
              </option>)}
          </select>
        </div>;
    }
    if (type === 'Adjust Loyalty Points') {
      return <div className="flex items-center gap-2" data-id="element-6122">
          <div className="flex bg-[#F1F5F9] rounded-[6px] p-0.5" data-id="element-6123">
            <button className={`px-2 py-1 text-xs font-medium rounded-[4px] transition-all ${!value.startsWith('-') ? 'bg-white shadow-sm text-[#0F172A]' : 'text-[#64748B]'}`} onClick={() => updateAction(isMet, action.id, 'value', value.replace('-', ''))} data-id="element-6124">
              <Plus className="w-3 h-3" data-id="element-6125" />
            </button>
            <button className={`px-2 py-1 text-xs font-medium rounded-[4px] transition-all ${value.startsWith('-') ? 'bg-white shadow-sm text-[#0F172A]' : 'text-[#64748B]'}`} onClick={() => updateAction(isMet, action.id, 'value', value.startsWith('-') ? value : `-${value}`)} data-id="element-6126">
              <Minus className="w-3 h-3" data-id="element-6127" />
            </button>
          </div>
          <input type="number" className={`w-24 ${conditionInputClasses}`} value={value.replace('-', '')} onChange={e => {
          const val = e.target.value;
          updateAction(isMet, action.id, 'value', value.startsWith('-') ? `-${val}` : val);
        }} data-id="element-6128" />
          <span className="text-sm text-[#64748B]" data-id="element-6129">points</span>
        </div>;
    }
    if (type === 'Add Note to Contact') {
      return <input type="text" placeholder="Note content..." className={`flex-1 ${conditionInputClasses}`} value={value} onChange={e => updateAction(isMet, action.id, 'value', e.target.value)} data-id="element-6130" />;
    }
    return null;
  };
  // --- Preview Generation ---
  const previewText = useMemo(() => {
    const triggerText = triggerType === 'Event-based' ? `When ${event.toLowerCase()} occurs` : 'Every day';
    const conditionsText = conditionGroups.map((group, i) => {
      const groupConditions = group.conditions.map(c => {
        let val = c.value;
        if (c.unit) val += ` ${c.unit}`;
        return `${c.field} ${c.operator} ${val}`;
      }).join(` ${group.logic} `);
      const prefix = i === 0 ? 'if ' : '';
      return `${prefix}(${groupConditions})`;
    }).join(' OR ');
    return <div className="text-sm text-[#475569] leading-relaxed" data-id="element-6131">
        {triggerText}, {conditionsText}, then:
        <ul className="list-disc pl-5 mt-2 space-y-1" data-id="element-6132">
          {metActions.map(a => <li key={a.id} data-id="element-6133">
              {a.type}{' '}
              {['Add Tag', 'Remove Tag'].includes(a.type) ? <span className="bg-[#E0E7FF] text-[#3730A3] border border-[#C7D2FE] text-xs px-2 py-0.5 rounded-full font-medium align-middle mx-1" data-id="element-6134">
                  {a.value || '...'}
                </span> : a.type === 'Send Klaviyo Event' ? <code className="bg-[#F1F5F9] px-1.5 py-0.5 rounded text-[#0F172A] text-xs font-mono mx-1" data-id="element-6135">
                  {a.value || '...'}
                </code> : <span className="font-medium text-[#0F172A]" data-id="element-6136">
                  {a.value || '...'}
                </span>}
            </li>)}
        </ul>
        {exitActions.length > 0 && <>
            <div className="mt-2" data-id="element-6137">
              Otherwise (when conditions no longer met):
            </div>
            <ul className="list-disc pl-5 mt-1 space-y-1" data-id="element-6138">
              {exitActions.map(a => <li key={a.id} data-id="element-6139">
                  {a.type}{' '}
                  {['Add Tag', 'Remove Tag'].includes(a.type) ? <span className="bg-[#E0E7FF] text-[#3730A3] border border-[#C7D2FE] text-xs px-2 py-0.5 rounded-full font-medium align-middle mx-1" data-id="element-6140">
                      {a.value || '...'}
                    </span> : <span className="font-medium text-[#0F172A]" data-id="element-6141">
                      {a.value || '...'}
                    </span>}
                </li>)}
            </ul>
          </>}
      </div>;
  }, [triggerType, event, conditionGroups, metActions, exitActions]);
  return <>
      <div className="block md:hidden" data-id="element-6142">
        <MobileUnsupported data-id="element-6143" />
      </div>

      <div className="hidden md:block bg-[#F1F5F9] min-h-full" data-id="element-6144">
        {/* Header Bar */}
        <nav className="w-full h-16 bg-white border-b border-[#CBD5E1] px-6 flex items-center gap-4 sticky top-0 z-10" data-id="element-6145">
          <h1 className="text-lg font-semibold text-[#0F172A] m-0" data-id="element-6146">
            Create Automation Rule
          </h1>
          <div aria-hidden="true" className="flex-1" data-id="element-6147"></div>
          <Button variant="ghost" data-id="element-6148">Save as Draft</Button>
          <Button variant="primary" data-id="element-6149">Activate Rule</Button>
          <NavActions data-id="element-6150" />
        </nav>

        <div className="p-6 max-w-5xl mx-auto" data-id="element-6151">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm mb-6" data-id="element-6152">
            <span className="text-[#64748B]" data-id="element-6153">Settings</span>
            <ChevronRight className="w-4 h-4 text-[#94A3B8]" data-id="element-6154" />
            <button onClick={() => navigate('/settings/automation-rules')} className="text-[#0EA5E9] hover:underline transition-colors duration-200" data-id="element-6155">
              Automation Rules
            </button>
            <ChevronRight className="w-4 h-4 text-[#94A3B8]" data-id="element-6156" />
            <span className="text-[#0F172A] font-medium" data-id="element-6157">Create Rule</span>
          </div>

          {/* Form Inputs */}
          <div className="mb-8 space-y-4" data-id="element-6158">
            <Input label="Rule Name" placeholder="e.g., Tag loyal members after 12 months" value={ruleName} onChange={e => setRuleName(e.target.value)} data-id="element-6159" />
            <Input label="Description" placeholder="Optional description for your team" value={description} onChange={e => setDescription(e.target.value)} data-id="element-6160" />
          </div>

          {/* Card 1: Trigger */}
          <Card padding="none" className="mb-6" data-id="element-6161">
            <div className="bg-[#F8FAFC] border-b border-[#E2E8F0] px-5 py-3 rounded-t-lg flex items-center gap-3" data-id="element-6162">
              <Zap className="w-5 h-5 text-[#F59E0B]" data-id="element-6163" />
              <div data-id="element-6164">
                <span className="text-sm font-semibold text-[#0F172A]" data-id="element-6165">
                  Trigger
                </span>
                <span className="text-xs text-[#64748B] ml-2" data-id="element-6166">
                  When should this rule be evaluated?
                </span>
              </div>
            </div>
            <div className="p-5" data-id="element-6167">
              <div className="grid grid-cols-2 gap-4 mb-3" data-id="element-6168">
                <div data-id="element-6169">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-6170">
                    Trigger Type
                  </label>
                  <select className={selectClasses} style={selectPadding} value={triggerType} onChange={e => setTriggerType(e.target.value as TriggerType)} data-id="element-6171">
                    <option value="Event-based" data-id="element-6172">Event-based</option>
                    <option value="Scheduled (Daily)" data-id="element-6173">Scheduled (Daily)</option>
                  </select>
                </div>
                {triggerType === 'Event-based' && <div data-id="element-6174">
                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-6175">
                      Event
                    </label>
                    <select className={selectClasses} style={selectPadding} value={event} onChange={e => setEvent(e.target.value)} data-id="element-6176">
                      {TRIGGER_EVENTS.map(evt => <option key={evt} value={evt} data-id="element-6177">
                          {evt}
                        </option>)}
                    </select>
                  </div>}
              </div>
              <p className="text-xs text-[#94A3B8]" data-id="element-6178">
                {triggerType === 'Event-based' ? `This rule will check conditions each time a "${event}" event occurs.` : 'This rule runs once per day and evaluates all active subscribers.'}
              </p>
            </div>
          </Card>

          {/* Card 2: Conditions */}
          <Card padding="none" className="mb-6" data-id="element-6179">
            <div className="bg-[#F8FAFC] border-b border-[#E2E8F0] px-5 py-3 rounded-t-lg flex items-center gap-3" data-id="element-6180">
              <Filter className="w-5 h-5 text-[#8B5CF6]" data-id="element-6181" />
              <div data-id="element-6182">
                <span className="text-sm font-semibold text-[#0F172A]" data-id="element-6183">
                  Conditions
                </span>
                <span className="text-xs text-[#64748B] ml-2" data-id="element-6184">
                  Only apply to members who match these criteria.
                </span>
              </div>
            </div>
            <div className="p-5" data-id="element-6185">
              {conditionGroups.map((group, index) => <div key={group.id} data-id="element-6186">
                  {index > 0 && <div className="flex items-center gap-3 my-4" data-id="element-6187">
                      <div className="h-px bg-[#E2E8F0] flex-1" data-id="element-6188"></div>
                      <span className="bg-[#F1F5F9] text-[#475569] text-xs px-3 py-1 rounded-full font-medium" data-id="element-6189">
                        OR
                      </span>
                      <div className="h-px bg-[#E2E8F0] flex-1" data-id="element-6190"></div>
                    </div>}

                  <div className="border border-[#E2E8F0] rounded-lg p-4" data-id="element-6191">
                    <div className="flex items-center justify-between mb-3" data-id="element-6192">
                      <span className="text-[12px] font-semibold text-[#94A3B8] uppercase tracking-[0.5px]" data-id="element-6193">
                        Group {index + 1}
                      </span>
                      <div className="flex bg-[#F1F5F9] rounded-[6px] p-0.5" data-id="element-6194">
                        <button onClick={() => toggleGroupLogic(group.id)} className={`px-2.5 py-1 text-xs font-medium rounded-[4px] transition-all duration-200 ${group.logic === 'AND' ? 'bg-[#0EA5E9] text-white shadow-sm' : 'text-[#475569] hover:bg-[#E2E8F0]'}`} data-id="element-6195">
                          AND
                        </button>
                        <button onClick={() => toggleGroupLogic(group.id)} className={`px-2.5 py-1 text-xs font-medium rounded-[4px] transition-all duration-200 ${group.logic === 'OR' ? 'bg-[#0EA5E9] text-white shadow-sm' : 'text-[#475569] hover:bg-[#E2E8F0]'}`} data-id="element-6196">
                          OR
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3" data-id="element-6197">
                      {group.conditions.map(condition => <div key={condition.id} className="flex items-center gap-3" data-id="element-6198">
                          <select className={`flex-[3] min-w-0 ${conditionSelectClasses}`} style={selectPadding} value={condition.field} onChange={e => updateCondition(group.id, condition.id, 'field', e.target.value)} data-id="element-6199">
                            {Object.entries(CONDITION_FIELDS).map(([category, fields]) => <optgroup key={category} label={category} data-id="element-6200">
                                  {fields.map(f => <option key={f} value={f} data-id="element-6201">
                                      {f}
                                    </option>)}
                                </optgroup>)}
                          </select>

                          <select className={`flex-[4] min-w-0 ${conditionSelectClasses}`} style={selectPadding} value={condition.operator} onChange={e => updateCondition(group.id, condition.id, 'operator', e.target.value)} data-id="element-6202">
                            {getOperators(condition.field).map(op => <option key={op} value={op} data-id="element-6203">
                                {op}
                              </option>)}
                          </select>

                          {renderConditionValueInput(condition, group.id)}

                          <IconButton icon={<Trash2 className="w-4 h-4" data-id="element-6205" />} variant="ghost" label="Delete condition" className="text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#FEE2E2] flex-shrink-0" onClick={() => deleteCondition(group.id, condition.id)} data-id="element-6204" />
                        </div>)}
                    </div>

                    <button onClick={() => addCondition(group.id)} className="text-[#0EA5E9] text-xs font-medium mt-3 hover:underline transition-colors duration-200" data-id="element-6206">
                      + Add condition
                    </button>
                  </div>
                </div>)}

              <button onClick={addConditionGroup} className="text-[#0EA5E9] text-sm font-medium mt-4 hover:underline transition-colors duration-200" data-id="element-6207">
                + Add condition group
              </button>

              {/* Info Card */}
              <div className="bg-[#E0F2FE] border border-[#BAE6FD] rounded-lg p-4 mt-4 flex items-start gap-3" data-id="element-6208">
                <Info className="w-5 h-5 text-[#0EA5E9] flex-shrink-0 mt-0.5" data-id="element-6209" />
                <p className="text-sm text-[#0369A1]" data-id="element-6210">
                  Members who match these conditions will be processed.
                  Currently <strong data-id="element-6211">156 members</strong> match.
                </p>
              </div>
            </div>
          </Card>

          {/* Card 3: Actions */}
          <Card padding="none" className="mb-6" data-id="element-6212">
            <div className="bg-[#F8FAFC] border-b border-[#E2E8F0] px-5 py-3 rounded-t-lg flex items-center gap-3" data-id="element-6213">
              <Zap className="w-5 h-5 text-[#10B981]" data-id="element-6214" />
              <div data-id="element-6215">
                <span className="text-sm font-semibold text-[#0F172A]" data-id="element-6216">
                  Actions
                </span>
                <span className="text-xs text-[#64748B] ml-2" data-id="element-6217">
                  What should happen when conditions are met?
                </span>
              </div>
            </div>
            <div className="p-5" data-id="element-6218">
              {/* Section 1: Met */}
              <div className="border-l-4 border-[#10B981] pl-4 mb-6" data-id="element-6219">
                <h3 className="text-sm font-semibold text-[#0F172A] mb-3" data-id="element-6220">
                  When conditions are MET
                </h3>
                <div className="space-y-3" data-id="element-6221">
                  {metActions.map(action => <div key={action.id} className="flex items-center gap-3" data-id="element-6222">
                      <select className={`w-48 ${conditionSelectClasses}`} style={selectPadding} value={action.type} onChange={e => updateAction(true, action.id, 'type', e.target.value)} data-id="element-6223">
                        {ACTION_TYPES.map(t => <option key={t} value={t} data-id="element-6224">
                            {t}
                          </option>)}
                      </select>
                      {renderActionInput(action, true)}
                      <IconButton icon={<Trash2 className="w-4 h-4" data-id="element-6226" />} variant="ghost" label="Delete action" className="text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#FEE2E2]" onClick={() => deleteAction(true, action.id)} data-id="element-6225" />
                    </div>)}
                </div>
                <button onClick={() => addAction(true)} className="text-[#0EA5E9] text-xs font-medium mt-3 hover:underline transition-colors duration-200" data-id="element-6227">
                  + Add action
                </button>
              </div>

              {/* Section 2: Not Met */}
              <div className="border-l-4 border-[#EF4444] pl-4" data-id="element-6228">
                <h3 className="text-sm font-semibold text-[#0F172A] mb-3" data-id="element-6229">
                  When conditions are NO LONGER MET (exit criteria)
                </h3>
                <div className="space-y-3" data-id="element-6230">
                  {exitActions.map(action => <div key={action.id} className="flex items-center gap-3" data-id="element-6231">
                      <select className={`w-48 ${conditionSelectClasses}`} style={selectPadding} value={action.type} onChange={e => updateAction(false, action.id, 'type', e.target.value)} data-id="element-6232">
                        {ACTION_TYPES.map(t => <option key={t} value={t} data-id="element-6233">
                            {t}
                          </option>)}
                      </select>
                      {renderActionInput(action, false)}
                      <IconButton icon={<Trash2 className="w-4 h-4" data-id="element-6235" />} variant="ghost" label="Delete action" className="text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#FEE2E2]" onClick={() => deleteAction(false, action.id)} data-id="element-6234" />
                    </div>)}
                </div>
                <button onClick={() => addAction(false)} className="text-[#0EA5E9] text-xs font-medium mt-3 hover:underline transition-colors duration-200" data-id="element-6236">
                  + Add action
                </button>
              </div>

              <p className="text-xs text-[#94A3B8] mt-4" data-id="element-6237">
                Exit criteria are checked daily. When a member no longer matches
                the conditions, the exit actions are triggered automatically.
              </p>
            </div>
          </Card>

          {/* Preview Panel */}
          <Card padding="none" className="bg-[#F8FAFC]" data-id="element-6238">
            <div className="p-5" data-id="element-6239">
              <h3 className="text-sm font-semibold text-[#0F172A] mb-2" data-id="element-6240">
                Rule Preview
              </h3>
              {previewText}
            </div>
          </Card>
        </div>
      </div>
    </>;
}